/**
 * Model Selector Component - Claude Style
 *
 * Dropdown to select AI model in chat input area
 * Shows model name, provider badge, and thinking indicator
 */

import * as React from 'react'
import { ChevronDown, Sparkles, Brain, Zap, Cloud } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface AIModel {
  id: string
  name: string
  provider: string
  supportsThinking: boolean
}

interface ModelSelectorProps {
  models: AIModel[]
  selectedModel: string | null
  onSelectModel: (modelId: string) => void
  disabled?: boolean
  className?: string
}

// Provider icons and colors
const providerConfig: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  anthropic: { icon: Sparkles, color: 'text-orange-400', label: 'Anthropic' },
  openai: { icon: Zap, color: 'text-emerald-400', label: 'OpenAI' },
  google: { icon: Brain, color: 'text-blue-400', label: 'Google' },
  azure: { icon: Cloud, color: 'text-sky-400', label: 'Azure' },
}

export function ModelSelector({
  models,
  selectedModel,
  onSelectModel,
  disabled = false,
  className,
}: ModelSelectorProps) {
  const currentModel = models.find((m) => m.id === selectedModel) ?? models[0]

  // Group models by provider
  const groupedModels = React.useMemo(() => {
    const groups: Record<string, AIModel[]> = {}
    for (const model of models) {
      if (!groups[model.provider]) {
        groups[model.provider] = []
      }
      const providerGroup = groups[model.provider]
      if (providerGroup) {
        providerGroup.push(model)
      }
    }
    return groups
  }, [models])

  if (!currentModel) {
    return null
  }

  const providerCfg = providerConfig[currentModel.provider]
  const ProviderIcon = providerCfg?.icon ?? Sparkles
  const providerColor = providerCfg?.color ?? 'text-muted-foreground'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-8 gap-1.5 px-2 text-sm font-medium',
            'text-muted-foreground hover:text-foreground',
            'focus-visible:ring-1 focus-visible:ring-ring',
            disabled && 'opacity-50 cursor-not-allowed',
            className
          )}
        >
          <ProviderIcon className={cn('h-3.5 w-3.5', providerColor)} />
          <span className="max-w-[120px] truncate">{getShortName(currentModel.name)}</span>
          {currentModel.supportsThinking && (
            <span className="flex h-4 w-4 items-center justify-center rounded bg-primary/20">
              <Brain className="h-2.5 w-2.5 text-primary" />
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Select Model
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {Object.entries(groupedModels).map(([provider, providerModels], idx) => {
          const config = providerConfig[provider]
          const Icon = config?.icon || Sparkles

          return (
            <React.Fragment key={provider}>
              {idx > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className="flex items-center gap-2 text-xs">
                <Icon className={cn('h-3 w-3', config?.color)} />
                {config?.label || provider}
              </DropdownMenuLabel>

              {providerModels.map((model) => (
                <DropdownMenuItem
                  key={model.id}
                  onClick={() => onSelectModel(model.id)}
                  className={cn(
                    'flex items-center justify-between gap-2 cursor-pointer',
                    model.id === selectedModel && 'bg-accent'
                  )}
                >
                  <span className="truncate">{model.name}</span>
                  {model.supportsThinking && (
                    <span className="flex items-center gap-1 text-xs text-primary">
                      <Brain className="h-3 w-3" />
                      Think
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
            </React.Fragment>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Get shortened display name
function getShortName(name: string): string {
  // Map full names to short versions
  const shortNames: Record<string, string> = {
    'Claude Opus 4.5': 'Opus 4.5',
    'Claude Sonnet 4': 'Sonnet 4',
    'GPT 5.2 Turbo': 'GPT 5.2',
    'GPT 4.1 Turbo': 'GPT 4.1',
    'OpenAI o3 Reasoning': 'o3',
    'Gemini 3 Pro': 'Gemini 3',
    'Gemini 2.5 Flash': 'Gemini 2.5',
    'Azure GPT 4.1': 'Azure 4.1',
    'Azure GPT 5.2': 'Azure 5.2',
  }

  return shortNames[name] || name
}
